import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";

import { useFetch } from "@/lib/axios.config";
import { PaginationResponse } from "@/shared/types/api.types";
import { ChallengeMember } from "../types";
import { appConfig } from "@/lib/site-config";
import {
  initialPageParam,
  getNextPageParam,
  getPreviousPageParam,
} from "@/shared/utils/query";

export enum ChallengeMemberKeys {
  ChallengeMembers = "challengeMembers",
}

type Winner = boolean | null;

interface FetchMemberListInput {
  pageParam: string | number;
  q?: string;
  challengeId: string | undefined;
  limit?: number;
  winner?: Winner;
}

const fetchMemberList = async <T>(props: FetchMemberListInput): Promise<T> => {
  const { pageParam, q, challengeId, limit, winner = null } = props;
  const response = await useFetch.get<T>(
    `/challenges/ChallengeParticipantList/${challengeId}`,
    {
      params: {
        limit: limit ?? appConfig.infiniteQueryLimit,
        offset: pageParam,
        search: q,
        winner,
      },
    }
  );

  return response.data;
};

export const useChallengeMembers = (
  challengeId: string | undefined,
  q?: string,
  winner: Winner = null
) => {
  return infiniteQueryOptions({
    queryKey: [ChallengeMemberKeys.ChallengeMembers, challengeId, q],
    queryFn: async ({ pageParam }) => {
      return fetchMemberList<PaginationResponse<ChallengeMember>>({
        pageParam,
        q,
        challengeId,
        winner,
      });
    },
    enabled: !!challengeId,
    initialPageParam,
    getNextPageParam: getNextPageParam,
    getPreviousPageParam: getPreviousPageParam,
  });
};

export const useChallengeLimitedMembers = (
  challengeId: string | undefined,
  winner: Winner = null,
  limit = 5
) => {
  return queryOptions({
    queryKey: [ChallengeMemberKeys.ChallengeMembers, challengeId],
    queryFn: async () => {
      return fetchMemberList<PaginationResponse<ChallengeMember>>({
        pageParam: 0,
        challengeId,
        limit: limit,
        winner,
      });
    },
    enabled: !!challengeId,
  });
};
